import {
  Banner,
  LegacyCard,
  Icon,
  Layout,
  Page,
  LegacyStack,
  SkeletonBodyText,
  SkeletonPage,
  Tabs,
  Tag,
  TextContainer,
  Button,
} from '@shopify/polaris';
import { HashtagMinor } from '@shopify/polaris-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getProdutById } from '../../../services/productService';
import ProductStockTable from '../../UI/Tables/ProductStockTable';
import { postAdjustment } from '../../../services/depositsService';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { checkPermission } from '../../../services/permissionsService';
import DropdownButton from '../../UI/Common/DropdownButton';
import ReclassifyModal from '../../UI/Products/ReclassifyModal';
import ReconcileLossesModal from '../../UI/Products/ReconcileLossesModal';
import ProductAliasesTab from '../../UI/Products/ProductAliasesTab';
import PvpTag from '../../UI/Products/PvpTag';
import ProductHistoryTimeline from '../../UI/Products/ProductHistoryTimeline';

// Tabs de la zona de stock: la tabla de stock vivo y el historial de movimientos.
const STOCK_TABS = [
  { id: 'stock', content: 'Stock' },
  { id: 'history', content: 'Historial' },
];

const ViewProduct = () => {
  const { id } = useParams();
  const [selected, setSelected] = useState(0);
  const [stockTab, setStockTab] = useState(0);
  const [modifyStock, setModifyStock] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'reclassify' | 'reconcile' | null
  const handleTabChange = useCallback((selectedTabIndex) => setSelected(selectedTabIndex), []);
  const [, dispatchToast] = useContext(ToastContext);

  const returnMatch = window.location.search.match(/\??r=(.+)&?/);
  const returnUrl = returnMatch && returnMatch.length > 1 ? window.atob(returnMatch[1]) : '/products';

  // react-query para la carga del producto
  const {
    data: productData,
    isLoading,
    isError,
    refetch,
  } = useQuery(['product', id], () => getProdutById(id).then((r) => r.data), {
    enabled: !!id,
  });

  // Estado local para manejar edición de stock (mutable)
  const [product, setProduct] = useState(null);
  const [productBefore, setProductBefore] = useState(null);

  useEffect(() => {
    if (productData) {
      setProduct(JSON.parse(JSON.stringify(productData)));
      setProductBefore(JSON.parse(JSON.stringify(productData)));
    }
  }, [productData]);

  const tabs = product
    ? [
        {
          id: 'description',
          content: 'Descripción',
          panel: <Descripcion product={product} />,
        },
        {
          id: 'referencias',
          content: 'Referencias',
          panel: <Referencias product={product} />,
        },
        {
          id: 'alias',
          content: 'Alias',
          panel: <ProductAliasesTab product={product} />,
        },
      ]
    : [];

  const handleSaveModifiedStock = () => {
    const stockBefore = productBefore.stock;
    const stockAfter = product.stock;

    const stockDifferences = stockAfter
      .map(({ barcode, qty, deposit }) => {
        const stock = stockBefore.find((s) => s.deposit.id === deposit.id && s.barcode === barcode);
        if (!stock) return { qty, deposit, barcode };

        return { qty: qty - stock.qty, deposit, barcode };
      })
      .filter(({ qty }) => qty !== 0)
      .reduce((acc, { qty, deposit, barcode }) => {
        acc[deposit.id] = acc[deposit.id] || [];
        acc[deposit.id].push({ qty, barcode });
        return acc;
      }, {});

    Promise.all(
      Object.keys(stockDifferences).map((depositId) => postAdjustment(depositId, stockDifferences[depositId])),
    )
      .then(() => {
        setModifyStock(false);
        dispatchToast({ type: SET_OK, payload: { message: 'Ajustes realizados' } });
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: { message: 'Error al realizar los ajustes' } });
      })
      .finally(() => refetch());
  };

  const handleCancelModifiedStock = () => {
    setProduct(JSON.parse(JSON.stringify(productData)));
    setModifyStock(false);
  };

  const handleModifyStock = ({ qty, deposit, barcode }) => {
    const { stock } = product;
    const stockEle = stock.find((s) => s.deposit.id === deposit.id && s.barcode === barcode);

    if (stockEle) {
      stockEle.qty = qty;
    } else {
      stock.push({ qty, deposit, barcode });
    }

    setProduct({ ...product });
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <SkeletonPage primaryAction>
        <Layout>
          <Layout.Section>
            <LegacyCard>
              <LegacyCard.Section>
                <TextContainer>
                  <SkeletonBodyText lines={6} />
                </TextContainer>
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
          <Layout.Section>
            <LegacyCard>
              <LegacyCard.Section>
                <TextContainer>
                  <SkeletonBodyText lines={4} />
                </TextContainer>
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  // Error state
  if (isError) {
    return (
      <Page title="Error" backAction={{ content: 'Ver productos', url: returnUrl }}>
        <Banner status="critical">
          <p>No se pudo cargar el producto. Comprueba que el ID es correcto.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <>
      {product && (
        <>
        <Page
          title={product.title}
          subtitle={product.family}
          backAction={{ content: 'Ver productos', url: returnUrl }}
          secondaryActions={
            <LegacyStack spacing="tight" alignment="center">
              <Button size="slim" url={`/products/${id}/edit`} variant="plain">
                Editar Producto
              </Button>
              {checkPermission('STOCK:UPDATE') && (
                <DropdownButton
                  title="Acciones"
                  buttonProps={{ size: 'slim' }}
                  items={[
                    { content: 'Reclasificar a otra familia', onAction: () => setModalMode('reclassify') },
                    { content: 'Reconciliar extraviados', onAction: () => setModalMode('reconcile') },
                  ]}
                />
              )}
            </LegacyStack>
          }
          divider
        >
          <>
            <Layout>
              <Layout.Section>
                <LegacyCard>
                  <LegacyCard.Section>
                    <section className="md:flex md:justify-between">
                      <div className="md:w-1/4">
                        <img src={`${product.images[0]}`} alt={product.family} className="object-contain" />
                      </div>
                      <div className="md:w-2/3">
                        <LegacyStack>
                          <Tag>
                            <LegacyStack spacing="extraTight">
                              <Icon source={HashtagMinor} color="base" />
                              <span>{product.family}</span>
                            </LegacyStack>
                          </Tag>
                          <PvpTag pvpMin={product.pvp_min} pvpMax={product.pvp_max} />
                        </LegacyStack>

                        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
                          {tabs[selected].panel}
                        </Tabs>
                      </div>
                    </section>
                  </LegacyCard.Section>
                </LegacyCard>
              </Layout.Section>

              <Layout.Section>
                {/* Stock actual e historial de movimientos comparten card, en tabs.
                    El card se renderiza dos veces (una por tab) porque las acciones
                    de cabecera (Modificar Stock) solo aplican al tab de stock. */}
                {stockTab === 0 ? (
                  <LegacyCard
                    title="Stock en todos los almacenes"
                    actions={
                      checkPermission('STOCK:UPDATE') && [
                        {
                          content: !modifyStock ? 'Modificar Stock' : 'Cancelar',
                          onAction: () => (!modifyStock ? setModifyStock(!modifyStock) : handleCancelModifiedStock()),
                        },
                      ]
                    }
                    primaryFooterAction={modifyStock && { content: 'Guardar cambios', onAction: handleSaveModifiedStock }}
                  >
                    <Tabs tabs={STOCK_TABS} selected={stockTab} onSelect={setStockTab} />
                    <LegacyCard.Section>
                      <ProductStockTable product={product} modifyStock={modifyStock} onModifyStock={handleModifyStock} />
                    </LegacyCard.Section>
                  </LegacyCard>
                ) : (
                  <LegacyCard title="Historial de movimientos">
                    <Tabs tabs={STOCK_TABS} selected={stockTab} onSelect={setStockTab} />
                    <ProductHistoryTimeline productId={id} variants={product.variants} />
                  </LegacyCard>
                )}
              </Layout.Section>
            </Layout>
          </>
        </Page>
          <ReclassifyModal
            open={modalMode === 'reclassify'}
            onClose={() => setModalMode(null)}
            product={product}
            onDone={refetch}
          />
          <ReconcileLossesModal
            open={modalMode === 'reconcile'}
            onClose={() => setModalMode(null)}
            product={product}
            onDone={refetch}
          />
        </>
      )}
    </>
  );
};

const Descripcion = ({ product }) => (
  <>
    <LegacyCard.Section title="">
      <>
        <div className="lg:w-1/2">{product.description}</div>
      </>
    </LegacyCard.Section>
    <LegacyCard.Section>
      <LegacyStack spacing="extraTight">
        {product.category && (
          <Tag>
            <LegacyStack spacing="extraTight">
              <span>{product.category}</span>
            </LegacyStack>
          </Tag>
        )}
        {product.subcategory && (
          <Tag>
            <LegacyStack spacing="extraTight">
              <span>{product.subcategory}</span>
            </LegacyStack>
          </Tag>
        )}
        {product.season && (
          <Tag>
            <LegacyStack spacing="extraTight">
              <span>{product.season}</span>
            </LegacyStack>
          </Tag>
        )}
        {product.year && product.season !== 'BASICO' && (
          <Tag>
            <LegacyStack spacing="extraTight">
              <span>{product.year}</span>
            </LegacyStack>
          </Tag>
        )}
      </LegacyStack>
    </LegacyCard.Section>
    <LegacyCard.Section title="Detalles y Cuidados">
      <div dangerouslySetInnerHTML={{ __html: product.detail }} />
    </LegacyCard.Section>
  </>
);

const Referencias = ({ product }) => (
  <LegacyCard.Section>
    <ul>
      <li className="flex justify-between border-b border-gray-100 font-semibold">
        <span className="w-1/4">Talla</span>
        <span className="w-1/3">SKU MIRTO</span>
        <span className="w-1/3">EAN MIRTO</span>
        <span className="w-1/3">SKU EPH</span>
      </li>
      {product.variants.map((variant, k) => (
        <li key={k} className="flex justify-between border-b border-gray-100 py-1 ">
          <span className="w-1/4 ">{variant.title}</span>
          <span className="w-1/3">{variant.sku}</span>
          <span className="w-1/3">{variant.barcode}</span>
          <span className="w-1/3">{variant.upcEph}</span>
        </li>
      ))}
    </ul>
  </LegacyCard.Section>
);

export default ViewProduct;
